/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2026 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2026 - Finnish Meteorological Institute (FMI)
 * Copyright 2026 - The Norwegian Meteorological Institute (MET Norway)
 * */

import { Box, Portal } from '@mui/material';
import React from 'react';
import { CustomIconButton } from '@opengeoweb/shared';
import { ViewSettingsIcon } from '@opengeoweb/theme';
import { usePluginsTranslation } from '../../utils/i18n';
import { PluginComponentConnect } from '../PluginComponentConnect';
import { DraggablePlugin } from './DraggablePlugin';
import { OverlayPluginMenuList } from './OverlayPluginMenuList';

interface ActivePlugin {
  pluginId: string;
  portalTarget: HTMLElement | null;
}

interface OverlayPluginMenuButtonProps {
  panelId: string;
}

export const OverlayPluginMenuButton = ({
  panelId,
}: OverlayPluginMenuButtonProps): React.ReactElement => {
  const { t } = usePluginsTranslation();
  const [open, setOpen] = React.useState(false);
  const [activePlugins, setActivePlugins] = React.useState<ActivePlugin[]>([]);
  const buttonRef = React.useRef<HTMLDivElement>(null);

  const toggleMenu = (): void => {
    setOpen((prev) => !prev);
  };

  const handlePluginToggle = (pluginId: string, isActive: boolean): void => {
    if (isActive) {
      const mosaicWindow =
        buttonRef.current?.closest<HTMLElement>('.mosaic-window');
      const portalTarget =
        mosaicWindow?.querySelector<HTMLElement>('.mosaic-window-body') ?? null;
      setActivePlugins((prev) => [...prev, { pluginId, portalTarget }]);
    } else {
      setActivePlugins((prev) =>
        prev.filter((plugin) => plugin.pluginId !== pluginId),
      );
    }
  };

  const activePluginIds = activePlugins.map((plugin) => plugin.pluginId);

  return (
    <Box ref={buttonRef} sx={{ position: 'relative', display: 'inline-block' }}>
      <CustomIconButton
        data-testid="overlay-plugin-menu-button"
        onClick={toggleMenu}
        tooltipTitle={
          !open ? t('open-overlay-plugin-menu') : t('close-overlay-plugin-menu')
        }
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <ViewSettingsIcon />
        </Box>
      </CustomIconButton>

      {open ? (
        <Box sx={{ position: 'absolute', top: '120%', right: 0, zIndex: 1400 }}>
          <OverlayPluginMenuList
            activePluginIds={activePluginIds}
            onPluginToggle={handlePluginToggle}
          />
        </Box>
      ) : null}

      {activePlugins.map(({ pluginId, portalTarget }) => (
        <Portal container={portalTarget} key={pluginId}>
          <DraggablePlugin>
            <PluginComponentConnect
              pluginId={pluginId}
              sourceId={`${panelId}-${pluginId}`}
            />
          </DraggablePlugin>
        </Portal>
      ))}
    </Box>
  );
};
